import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useIntl } from "react-intl";
import Alert from "../../components/alert/Alert";
import InfoIcon from "../../components/icons/Info";
import LinkIcon from "../../components/icons/Link";
import DeleteIcon from "../../components/icons/Delete";
import ArrowLeft from "../../components/icons/ArrowLeft";
import useEquipment, { EquipmentPayload } from "../../hooks/useEquipment";
import useSites from "../../hooks/useSites";
import { useLocalizedPath } from "../../hooks/useLocale";
import PageSectionHeader from "../../components/PageSectionHeader";

type ObjectFormData = EquipmentPayload;
type ActionType = "load" | "create" | "update" | "delete" | null;

const defaultFormData: ObjectFormData = {
  objectId: "",
  siteId: null,
  building: "",
  designation: "",
  elementType: "",
  location: "",
  repere: "",
  SapPosteTechnique: "",
  status: "active",
  ealicoLink: "",
  ealicoLinkTitle: "",
  ealicoLink2: "",
  ealicoLink2Title: "",
  otherLink1Title: "",
  otherLink1Url: "",
  otherLink2Title: "",
  otherLink2Url: "",
  SapEquipement: "",
};

const AddObject = () => {
  const topRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const localizedPath = useLocalizedPath();
  const [searchParams] = useSearchParams();
  const objectIdParam = searchParams.get("id");
  const isUpdateMode = !!objectIdParam;
  const listSearch = (location.state as { listSearch?: string } | null)?.listSearch;

  const goBackToList = useCallback(() => {
    navigate({
      pathname: localizedPath("/dashboard/admin/objects"),
      search: listSearch ?? ""
    });
  }, [listSearch, localizedPath, navigate]);
  const [equipmentId, setEquipmentId] = useState<number | null>(null);
  const currentActionRef = useRef<ActionType>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const {
    error,
    isLoading,
    response,
    fetchEquipment,
    createEquipment,
    updateEquipment,
    removeEquipment
  } = useEquipment();
  const { sites, fetchSites } = useSites();

  const [formData, setFormData] = useState<ObjectFormData>({
    ...defaultFormData,
    objectId: objectIdParam ?? "",
  });

  const pageTitle = useMemo(() => {
    return isUpdateMode
      ? intl.formatMessage({ id: "equipment.form.titleEdit" }, { id: objectIdParam })
      : intl.formatMessage({ id: "equipment.form.titleAdd" });
  }, [isUpdateMode, objectIdParam, intl]);

  const documentTitle = useMemo(() => {
    return isUpdateMode
      ? intl.formatMessage(
          { id: "equipment.form.documentTitleEdit" },
          { id: objectIdParam, appName: import.meta.env.VITE_APP_NAME }
        )
      : intl.formatMessage(
          { id: "equipment.form.documentTitleAdd" },
          { appName: import.meta.env.VITE_APP_NAME }
        );
  }, [isUpdateMode, objectIdParam, intl]);

  const handleChange = <K extends keyof ObjectFormData>(key: K, value: ObjectFormData[K]) => {
    setFormData((current) => ({ ...current, [key]: value }));
  };

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  useEffect(() => {
    if (isUpdateMode && objectIdParam) {
      currentActionRef.current = "load";
      fetchEquipment(objectIdParam);
    }
  }, [fetchEquipment, isUpdateMode, objectIdParam]);

  useEffect(() => {
    if (!response || !currentActionRef.current) return;
    const action = currentActionRef.current;
    currentActionRef.current = null;

    if (action === "load") {
      if (response.data) {
        const { id, ...payload } = response.data;
        setEquipmentId(id);
        setFormData({
          ...defaultFormData,
          objectId: payload.objectId || objectIdParam || "",
          siteId: payload.siteId ?? null,
          building: payload.building ?? "",
          designation: payload.designation ?? "",
          elementType: payload.elementType ?? "",
          location: payload.location ?? "",
          repere: payload.repere ?? "",
          SapPosteTechnique: payload.SapPosteTechnique ?? "",
          status: payload.status ?? "active",
          ealicoLink: payload.ealicoLink ?? "",
          ealicoLinkTitle: payload.ealicoLinkTitle ?? "",
          ealicoLink2: payload.ealicoLink2 ?? "",
          ealicoLink2Title: payload.ealicoLink2Title ?? "",
          otherLink1Title: payload.otherLink1Title ?? "",
          otherLink1Url: payload.otherLink1Url ?? "",
          otherLink2Title: payload.otherLink2Title ?? "",
          otherLink2Url: payload.otherLink2Url ?? "",
          SapEquipement: payload.SapEquipement ?? "",
        });
      }
      return;
    }

    if (action === "create") {
      setSuccessMessage(
        response.message ?? intl.formatMessage({ id: "equipment.form.createdSuccess" })
      );
      if (response.data) {
        setEquipmentId(response.data.id);
      }
      return;
    }

    if (action === "update") {
      setSuccessMessage(
        response.message ?? intl.formatMessage({ id: "equipment.form.updatedSuccess" })
      );
      return;
    }

    if (action === "delete") {
      setSuccessMessage(intl.formatMessage({ id: "equipment.form.deletedSuccess" }));
      setEquipmentId(null);
      setFormData({ ...defaultFormData });
      return;
    }
  }, [objectIdParam, response, intl]);

  useEffect(() => {
    if (successMessage || error) {
      topRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [successMessage, error]);

  const onSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSuccessMessage(null);

    if (isUpdateMode && equipmentId) {
      currentActionRef.current = "update";
      updateEquipment(equipmentId, formData);
      return;
    }

    currentActionRef.current = "create";
    createEquipment(formData);
  }, [isUpdateMode, equipmentId, formData, updateEquipment, createEquipment]);

  const onDeleteEquipment = useCallback(() => {
    if (!equipmentId) return;
    const confirmed = window.confirm(
      intl.formatMessage({ id: "equipment.form.confirmDelete" })
    );
    if (!confirmed) return;
    setSuccessMessage(null);
    currentActionRef.current = "delete";
    removeEquipment(equipmentId);
  }, [equipmentId, removeEquipment, intl]);

  const onOpenEalico = () => {
    if (!formData.ealicoLink) return;
    window.open(formData.ealicoLink, "_blank", "noopener,noreferrer");
  };

  const onOpenEalico2 = () => {
    if (!formData.ealicoLink2) return;
    window.open(formData.ealicoLink2, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <Helmet>
        <title>{documentTitle}</title>
      </Helmet>

      <div className="h-full flex flex-col">
        <PageSectionHeader className="mb-5">
          <div ref={topRef}>
            <button
              type="button"
              onClick={goBackToList}
              className="mb-3 px-3 py-2 w-fit flex items-center gap-3 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
              style={{ color: "#023865" }}
            >
              <ArrowLeft className="w-3" />
              <span>{intl.formatMessage({ id: "common.back" })}</span>
            </button>
            <h2 className="text-2xl font-semibold" style={{ color: "#023865" }}>{pageTitle}</h2>
            <p className="text-sm text-gray-500 mt-1">
              {intl.formatMessage({ id: "equipment.form.subtitle" })}
            </p>
          </div>
        </PageSectionHeader>

        <form className="flex flex-col gap-5 pb-6" onSubmit={onSubmit}>
          {error && (
            <Alert
              className="w-full"
              type="error"
              autoHidden={false}
              message={error.message}
            />
          )}
          {successMessage && (
            <Alert
              className="w-full"
              type="success"
              autoHidden={false}
              message={successMessage}
            />
          )}

          <section className="bg-white rounded-2xl p-6" style={{ border: "1px solid #E4E7EC" }}>
            <div className="mb-4 flex flex-col gap-2">
              <InfoIcon />
              <h3 className="text-lg font-semibold text-gray-900">
                {intl.formatMessage({ id: "equipment.form.mainInfo" })}
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="mb-1 block text-sm text-gray-700" htmlFor="objectId">
                  {intl.formatMessage({ id: "equipment.form.objectId" })}
                </label>
                <input
                  id="objectId"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  type="text"
                  value={formData.objectId}
                  onChange={(e) => handleChange("objectId", e.target.value)}
                  placeholder="X86fh1"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm text-gray-700" htmlFor="siteId">
                  {intl.formatMessage({ id: "equipment.form.site" })}
                </label>
                <select
                  id="siteId"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  value={formData.siteId ?? ""}
                  onChange={(e) => handleChange("siteId", e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">{intl.formatMessage({ id: "equipment.form.selectSite" })}</option>
                  {sites?.map((site) => (
                    <option key={site.id} value={site.id}>{site.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm text-gray-700" htmlFor="building">
                  {intl.formatMessage({ id: "equipment.form.building" })}
                </label>
                <input
                  id="building"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  type="text"
                  value={formData.building}
                  onChange={(e) => handleChange("building", e.target.value)}
                  placeholder={intl.formatMessage({ id: "equipment.form.buildingPlaceholder" })}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm text-gray-700" htmlFor="designation">
                  {intl.formatMessage({ id: "equipment.form.designation" })}
                </label>
                <input
                  id="designation"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  type="text"
                  value={formData.designation}
                  onChange={(e) => handleChange("designation", e.target.value)}
                  placeholder={intl.formatMessage({ id: "equipment.form.designationPlaceholder" })}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm text-gray-700" htmlFor="elementType">
                  {intl.formatMessage({ id: "equipment.form.elementType" })}
                </label>
                <input
                  id="elementType"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  type="text"
                  value={formData.elementType}
                  onChange={(e) => handleChange("elementType", e.target.value)}
                  placeholder={intl.formatMessage({ id: "equipment.form.elementType" })}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm text-gray-700" htmlFor="location">
                  {intl.formatMessage({ id: "equipment.form.location" })}
                </label>
                <input
                  id="location"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleChange("location", e.target.value)}
                  placeholder={intl.formatMessage({ id: "equipment.form.locationPlaceholder" })}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm text-gray-700" htmlFor="repere">
                  {intl.formatMessage({ id: "equipment.form.repere" })}
                </label>
                <input
                  id="repere"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  type="text"
                  value={formData.repere}
                  onChange={(e) => handleChange("repere", e.target.value)}
                  placeholder={intl.formatMessage({ id: "equipment.form.reperePlaceholder" })}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm text-gray-700" htmlFor="SapPosteTechnique">
                  {intl.formatMessage({ id: "equipment.form.SapPosteTechnique" })}
                </label>
                <input
                  id="SapPosteTechnique"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  type="text"
                  value={formData.SapPosteTechnique}
                  onChange={(e) => handleChange("SapPosteTechnique", e.target.value)}
                  placeholder={intl.formatMessage({ id: "equipment.form.SapPosteTechniquePlaceholder" })}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm text-gray-700" htmlFor="SapEquipement">
                  {intl.formatMessage({ id: "equipment.form.SapEquipement" })}
                </label>
                <input
                  id="SapEquipement"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  type="text"
                  value={formData.SapEquipement}
                  onChange={(e) => handleChange("SapEquipement", e.target.value)}
                  placeholder={intl.formatMessage({ id: "equipment.form.SapEquipementPlaceholder" })}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm text-gray-700" htmlFor="status">
                  {intl.formatMessage({ id: "equipment.form.status" })}
                </label>
                <div className="relative">
                  <select
                    id="status"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    value={formData.status}
                    onChange={(e) => handleChange("status", e.target.value as ObjectFormData["status"])}
                  >
                    <option value="active">{intl.formatMessage({ id: "common.active" })}</option>
                    <option value="inactive">{intl.formatMessage({ id: "common.inactive" })}</option>
                  </select>
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                    {formData.status === "active"
                      ? intl.formatMessage({ id: "common.active" })
                      : intl.formatMessage({ id: "common.inactive" })}
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section className="bg-white rounded-2xl p-6" style={{ border: "1px solid #E4E7EC" }}>
            <div className="mb-4 flex flex-col gap-2">
              <LinkIcon />
              <h3 className="text-lg font-semibold text-gray-900">
                {intl.formatMessage({ id: "equipment.form.linksSection" })}
              </h3>
            </div>

            <div className="mb-6">
              <h4 className="mb-3 text-sm font-semibold text-gray-900">
                {intl.formatMessage({ id: "equipment.form.ealicoLink" })}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4">
                <div>
                  <label className="mb-1 block text-sm text-gray-700" htmlFor="ealicoLinkTitle">
                    {intl.formatMessage({ id: "equipment.form.linkTitle" })}
                  </label>
                  <input
                    id="ealicoLinkTitle"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    type="text"
                    value={formData.ealicoLinkTitle}
                    onChange={(e) => handleChange("ealicoLinkTitle", e.target.value)}
                    placeholder={intl.formatMessage({ id: "equipment.form.openInEalico" })}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm text-gray-700" htmlFor="ealicoLink">
                    {intl.formatMessage({ id: "equipment.form.linkUrl" })}
                  </label>
                  <input
                    id="ealicoLink"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    type="url"
                    value={formData.ealicoLink}
                    onChange={(e) => handleChange("ealicoLink", e.target.value)}
                    placeholder="https://ealico/assets/123"
                  />
                </div>
                <button
                  type="button"
                  className="self-end flex items-center gap-2 px-5 py-2.5 rounded-lg text-white"
                  style={{ backgroundColor: "#0066B3" }}
                  onClick={onOpenEalico}
                  disabled={!formData.ealicoLink}
                >
                  <LinkIcon className="shrink-0" />
                  {formData.ealicoLinkTitle.trim() ||
                    intl.formatMessage({ id: "equipment.form.openInEalico" })}
                </button>
              </div>
            </div>

            <div className="mb-6">
              <h4 className="mb-3 text-sm font-semibold text-gray-900">
                {intl.formatMessage({ id: "equipment.form.ealicoLink2" })}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4">
                <div>
                  <label className="mb-1 block text-sm text-gray-700" htmlFor="ealicoLink2Title">
                    {intl.formatMessage({ id: "equipment.form.linkTitle" })}
                  </label>
                  <input
                    id="ealicoLink2Title"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    type="text"
                    value={formData.ealicoLink2Title}
                    onChange={(e) => handleChange("ealicoLink2Title", e.target.value)}
                    placeholder={intl.formatMessage({ id: "equipment.form.openInEalico" })}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm text-gray-700" htmlFor="ealicoLink2">
                    {intl.formatMessage({ id: "equipment.form.linkUrl" })}
                  </label>
                  <input
                    id="ealicoLink2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    type="url"
                    value={formData.ealicoLink2}
                    onChange={(e) => handleChange("ealicoLink2", e.target.value)}
                    placeholder="https://ealico/assets/456"
                  />
                </div>
                <button
                  type="button"
                  className="self-end flex items-center gap-2 px-5 py-2.5 rounded-lg text-white"
                  style={{ backgroundColor: "#0066B3" }}
                  onClick={onOpenEalico2}
                  disabled={!formData.ealicoLink2}
                >
                  <LinkIcon className="shrink-0" />
                  {formData.ealicoLink2Title.trim() ||
                    intl.formatMessage({ id: "equipment.form.openInEalico" })}
                </button>
              </div>
            </div>

            <div className="mb-6">
              <h4 className="mb-3 text-sm font-semibold text-gray-900">
                {intl.formatMessage({ id: "equipment.form.otherLink1" })}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm text-gray-700" htmlFor="otherLink1Title">
                    {intl.formatMessage({ id: "equipment.form.linkTitle" })}
                  </label>
                  <input
                    id="otherLink1Title"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    type="text"
                    value={formData.otherLink1Title}
                    onChange={(e) => handleChange("otherLink1Title", e.target.value)}
                    placeholder={intl.formatMessage({ id: "equipment.form.linkTitle1Placeholder" })}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm text-gray-700" htmlFor="otherLink1Url">
                    {intl.formatMessage({ id: "equipment.form.linkUrl" })}
                  </label>
                  <input
                    id="otherLink1Url"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    type="url"
                    value={formData.otherLink1Url}
                    onChange={(e) => handleChange("otherLink1Url", e.target.value)}
                    placeholder="https://lien-1"
                  />
                </div>
              </div>
            </div>

            <div>
              <h4 className="mb-3 text-sm font-semibold text-gray-900">
                {intl.formatMessage({ id: "equipment.form.otherLink2" })}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm text-gray-700" htmlFor="otherLink2Title">
                    {intl.formatMessage({ id: "equipment.form.linkTitle" })}
                  </label>
                  <input
                    id="otherLink2Title"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    type="text"
                    value={formData.otherLink2Title}
                    onChange={(e) => handleChange("otherLink2Title", e.target.value)}
                    placeholder={intl.formatMessage({ id: "equipment.form.linkTitle2Placeholder" })}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm text-gray-700" htmlFor="otherLink2Url">
                    {intl.formatMessage({ id: "equipment.form.linkUrl" })}
                  </label>
                  <input
                    id="otherLink2Url"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    type="url"
                    value={formData.otherLink2Url}
                    onChange={(e) => handleChange("otherLink2Url", e.target.value)}
                    placeholder="https://lien-2"
                  />
                </div>
              </div>
            </div>
          </section>

          <div className="mt-1 flex items-center justify-between gap-3">
            <div>
              {isUpdateMode && (
                <button
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-white text-sm"
                  type="button"
                  style={{ backgroundColor: "#E94E1B" }}
                  disabled={isLoading || !equipmentId}
                  onClick={onDeleteEquipment}
                >
                  <DeleteIcon className="size-4 shrink-0" />
                  {intl.formatMessage({ id: "equipment.form.deleteEquipment" })}
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                className="px-4 py-2.5 rounded-lg border border-gray-300 text-sm text-gray-700"
                type="button"
                disabled={isLoading}
                onClick={goBackToList}
              >
                {intl.formatMessage({ id: "common.cancel" })}
              </button>
              <button
                className="px-4 py-2.5 rounded-lg text-sm text-white"
                type="submit"
                style={{ backgroundColor: "#0066B3" }}
                disabled={isLoading}
              >
                {isLoading
                  ? intl.formatMessage({ id: "common.saving" })
                  : intl.formatMessage({ id: "common.save" })}
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddObject;
